// MA-1056 — the website's "Set up a free class" entry: /teacher/start?src=<tag>.
//
// Captured once at app start (main.jsx), before the gate ladder can render the
// sign-in screen at whatever URL it is on, exactly like MA-1044's readiness
// check token (src/lib/check/pendingCheckToken.ts). localStorage (top level,
// first party, no cookie): the entry is a top-level link from the website, and
// both sign-in paths come back to this browser — Google's OAuth round trip
// returns to the app root in the same tab, and the email code is typed in the
// same tab (MA-941). The entry holds only the source tag (a short
// [a-z0-9_-] string, or null) and the time it was captured; no name, no email.
//
// What it drives, after sign-in:
//   - the front door (routes/index.tsx) sends a pending entry to the teacher
//     area's Class setup tab (/teacher?tab=setup) instead of the role default;
//   - the sign-in screen opens on "Signing in as a teacher" with the teacher
//     headline (unless this device already holds a family child: then step 1);
//     the role is written only when the visitor signs in; choosing "I'm a
//     parent" drops the entry; signOut drops it too (supabaseClient.js);
//   - the private-beta gate shows its teacher text;
//   - the teacher area clears the entry once it opens;
//   - useAuth records the tag on a NEW account's own auth user metadata
//     (`pw_signup_src`), first write wins, fire-and-forget: a sign-up is
//     attributed only when the account was created after the entry was
//     captured, so an existing account is never re-tagged.
// The entry expires PENDING_TEACHER_START_TTL_MS after capture (a shared
// family device must not keep routing the next sign-in to the teacher area).
// Storage is injectable for tests; every storage failure is swallowed.

export const PENDING_TEACHER_START_KEY = "pw_pending_teacher_start";
export const TEACHER_START_PATH = "/teacher/start";
/** The tab the entry lands on (TeacherShell's TEACHER_TABS id). */
export const TEACHER_START_TAB = "setup" as const;
/** Calibration value (not cited): long enough for a Google or email-code
 *  sign-in, short enough that a shared device forgets it the same hour. */
export const PENDING_TEACHER_START_TTL_MS = 60 * 60 * 1000;
/** Calibration value: the allowance for a client clock running ahead of the
 *  server's when deciding an account was created after the entry. */
export const SIGNUP_CLOCK_SKEW_MS = 10 * 60 * 1000;
/** The auth user metadata key the source tag is recorded under. The value is
 *  user-writable (any signed-in user can call updateUser), so it is
 *  attribution only: never read for authorization, billing or admission. */
export const SIGNUP_SRC_METADATA_KEY = "pw_signup_src";
/** The sign-in screen's headline while it is in teacher mode (a pre-seeded
 *  /teacher/start visit or a tap on "I'm a teacher"). Not spoken. */
export const TEACHER_SIGN_IN_HEADLINE = "Sign in to set up your class.";
/** The device's local family list (src/childrenStore.js REGISTRY_KEY). */
export const LOCAL_CHILDREN_KEY = "pencilwise-children";
/** Same shape as the early-access list's source tag (0068). */
const SRC_SHAPE = /^[a-z0-9_-]{1,32}$/;

export interface PendingTeacherStart {
  src: string | null;
  at: number;
  /** true once the source tag has been written to the account. */
  recorded?: boolean;
}

function store(s?: Storage): Storage | undefined {
  if (s) return s;
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** The tag, lower-cased and trimmed, or null when it is not a short
 *  [a-z0-9_-] tag (anything else is dropped, never stored). */
export function sanitizeTeacherStartSrc(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const t = raw.trim().toLowerCase();
  return SRC_SHAPE.test(t) ? t : null;
}

export function isTeacherStartPath(pathname: string): boolean {
  const p = (pathname || "").replace(/\/+$/, "");
  return p === TEACHER_START_PATH;
}

function write(entry: PendingTeacherStart, s?: Storage): boolean {
  try {
    const st = store(s);
    if (!st) return false;
    st.setItem(PENDING_TEACHER_START_KEY, JSON.stringify(entry));
    return true;
  } catch {
    return false;
  }
}

/** Store the entry when the URL is /teacher/start (with or without a tag). */
export function capturePendingTeacherStart(
  loc: { pathname: string; search: string },
  s?: Storage,
  now: number = Date.now(),
): boolean {
  if (!isTeacherStartPath(loc.pathname)) return false;
  let src: string | null = null;
  try {
    src = sanitizeTeacherStartSrc(new URLSearchParams(loc.search).get("src"));
  } catch {
    src = null;
  }
  return write({ src, at: now }, s);
}

/** The live entry, or null (none, unreadable, malformed, or expired). */
export function readPendingTeacherStart(s?: Storage, now: number = Date.now()): PendingTeacherStart | null {
  let raw: string | null = null;
  try {
    raw = store(s)?.getItem(PENDING_TEACHER_START_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<PendingTeacherStart> | null;
    if (!v || typeof v !== "object" || typeof v.at !== "number" || !Number.isFinite(v.at)) return null;
    if (now - v.at > PENDING_TEACHER_START_TTL_MS || v.at - now > SIGNUP_CLOCK_SKEW_MS) return null;
    return { src: sanitizeTeacherStartSrc(v.src), at: v.at, ...(v.recorded === true ? { recorded: true } : {}) };
  } catch {
    return null;
  }
}

/** true when this device's local family list holds at least one child. Any
 *  read failure or malformed value reads as false (no family known). */
export function deviceHasFamilyChildren(s?: Storage): boolean {
  try {
    const raw = store(s)?.getItem(LOCAL_CHILDREN_KEY) ?? null;
    if (!raw) return false;
    const v = JSON.parse(raw) as { children?: unknown } | null;
    return Array.isArray(v?.children) && v.children.length > 0;
  } catch {
    return false;
  }
}

export function clearPendingTeacherStart(s?: Storage): void {
  try {
    store(s)?.removeItem(PENDING_TEACHER_START_KEY);
  } catch {
    /* ignore */
  }
}

/** Where /teacher/start (and the front door, while an entry is pending)
 *  sends the visitor: the teacher area's Class setup tab. Every other query
 *  parameter is kept (the dev bypass and preview flags read the live URL);
 *  `src` is dropped from the address bar once captured. */
export function teacherStartDestination(search: Record<string, unknown> | undefined): {
  to: "/teacher";
  search: Record<string, unknown>;
} {
  const next: Record<string, unknown> = { ...(search ?? {}) };
  delete next.src;
  next.tab = TEACHER_START_TAB;
  return { to: "/teacher", search: next };
}

interface AuthUserLike {
  id?: string;
  created_at?: string;
  user_metadata?: Record<string, unknown> | null;
}

/** The tag to record on this account, or null: only a tagged, not yet
 *  recorded entry, for an account created after the entry was captured
 *  (less the clock-skew allowance) that carries no tag yet. */
export function signupSrcToRecord(user: AuthUserLike | null | undefined, entry: PendingTeacherStart | null): string | null {
  if (!user || !entry || !entry.src || entry.recorded) return null;
  const existing = user.user_metadata?.[SIGNUP_SRC_METADATA_KEY];
  if (existing !== undefined && existing !== null && existing !== "") return null;
  const created = typeof user.created_at === "string" ? Date.parse(user.created_at) : NaN;
  if (!Number.isFinite(created)) return null;
  if (created < entry.at - SIGNUP_CLOCK_SKEW_MS) return null;
  return entry.src;
}

const inFlight = new Set<string>();

interface AuthClientLike {
  auth: { updateUser: (attrs: { data: Record<string, unknown> }) => Promise<{ error?: unknown } | undefined> };
}

/**
 * Fire-and-forget: records the pending entry's tag on a new account (see
 * signupSrcToRecord). Returns the started write's promise (true when written)
 * for tests; callers ignore it. Never throws and never rejects. A failed
 * write leaves the entry unrecorded, so the next sign-in inside the TTL tries
 * once more.
 */
export function noteTeacherStartSignIn(
  user: AuthUserLike | null | undefined,
  client: AuthClientLike,
  s?: Storage,
  now: number = Date.now(),
): Promise<boolean> {
  try {
    const entry = readPendingTeacherStart(s, now);
    const src = signupSrcToRecord(user, entry);
    const id = user?.id ?? "";
    if (!src || !entry || !id || inFlight.has(id)) return Promise.resolve(false);
    inFlight.add(id);
    // Deferred a task: auth-js holds its lock while onAuthStateChange
    // callbacks run, and an auth call made inside one can deadlock.
    return new Promise<void>((resolve) => setTimeout(resolve, 0))
      .then(() => client.auth.updateUser({ data: { [SIGNUP_SRC_METADATA_KEY]: src } }))
      .then((res) => {
        if (res && res.error) return false;
        const still = readPendingTeacherStart(s, now);
        if (still && still.at === entry.at) write({ ...still, recorded: true }, s);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        inFlight.delete(id);
      });
  } catch {
    return Promise.resolve(false);
  }
}
